/**
 * adDomain.js — 광고 도메인 순수 계산 모듈 (단일 진실 소스, SoT)
 *
 * 근거: code-vue/src/views/backend/Ad/adSourcing.theory.md
 *   §0 통합마진 / §2~§3 핵심 공식 / §10·§12 광고타입 분류 / §15 ROAS↔CPC 환산
 *
 * CommonJS로 작성 — server.js(Node `require`)와 code-vue(webpack `import`)가 공용한다.
 * (eslint env.node=true 라 프론트에서도 module.exports 허용. 배포 시 양쪽에 code-vue/src 포함)
 *
 * ⚠️ 순수함수만. Vue/DOM/$store/네트워크 의존 금지. 입력은 전부 인자로 주입받는다.
 */

const lotCost = require('./lotCost');

// 쿠팡 CPC 최소값 (§0)
const CPC_MIN = 100;
// growthdb에 마진이 없을 때 기본 통합마진 (§0, adSourcing defaultMargin)
const DEFAULT_MARGIN = 5000;

/**
 * §0 통합마진 — 그 영역(검색/비검색/오디언스) 클릭으로 팔린 모든 상품(halo)의 마진 합.
 *   totalSum = Σ( growthdb 마진(판매상품) × 판매수량 )
 * @param {Array<{vendor_item_id:*, total_unit_14_days:number}>} salesList 판매상품 리스트
 * @param {(vendorItemId:*) => (number|undefined)} growthMarginOf vendorItemId → growthdb 마진 조회
 * @param {number} [defaultMargin] 마진 없을 때 기본값
 * @returns {number} totalSum (영역 통합마진 총합)
 */
function integratedMarginSum(salesList, growthMarginOf, defaultMargin = DEFAULT_MARGIN) {
  let totalSum = 0;
  if (Array.isArray(salesList)) {
    salesList.forEach(function (f) {
      // 원본과 동일하게 || 사용 (마진 0/undefined → 기본값)
      const margin = growthMarginOf(f.vendor_item_id) || defaultMargin;
      totalSum += margin * f.total_unit_14_days;
    });
  }
  return totalSum;
}

/**
 * §0 건당 통합마진 m = 총수익 / 주문수 (주문 0이면 0).
 * @param {number} totalSum integratedMarginSum 결과
 * @param {number} orders 주문수
 * @returns {number} m (정수)
 */
function marginPerOrder(totalSum, orders) {
  return orders > 0 ? parseInt(totalSum / orders, 10) : 0;
}

/** §0 순수익 = 총수익 − 광고비 */
function adProfit(totalSum, adCost) {
  return totalSum - adCost;
}

/**
 * §0 단위 마진 — growthdb 상품의 건당(단위당) 통합마진. growthdb 화면·store·광고 마진맵이 같이 쓴다(SoT).
 *   마진 = round((판매가 − 쿠폰 − 판매비용×1.1 − 원가) × 10/11)   (원가 = 매입가 + 밀크런비용, ×10/11 = VAT 제외)
 * 매입가(원가)가 없으면 마진 산출 불가 → null (호출측에서 기본통합마진으로 대체).
 *
 * basis(#112) — 판매장부와 같은 근거로 맞추는 값. /api/growth/getUnitCostBasis 가 옵션ID별로 준다.
 *   { 매입가, 밀크런, 배송비, 입출고비 }  (금액은 VAT 별도, 개당 · 각 칸 null 이면 아래 기본값)
 *   - 매입가·밀크런: 지금 팔리는 입고 lot 의 값(lotCost.fifoLotByDate — 판매장부 FIFO 와 같은 lot)
 *   - 배송비: 쿠팡은 배송비를 **주문(배송) 1건당** 받는다. 최근 실제 배송비 ÷ 순판매(반품 제외)
 *   - 입출고비: 실제 입출고비 ÷ 순판매(반품분까지 남은 판매가 나눠 짐 — 판매장부 개당이익과 같은 분모)
 * basis 가 없으면 예전과 같다: 매입가 = 대표매입가, 밀크런 = g.밀크런비용(최신 입고), 판매비용 = g.commission(쿠팡 요율 개당).
 * @param {{salePrice:number, coupon:number, commission:number, 수수료:Object, 매입가:number, 매입정보:Array, 밀크런비용:number}} g growthdb 상품
 * @param {{매입가:?number, 밀크런:?number, 배송비:?number, 입출고비:?number}} [basis]
 * @returns {number|null}
 */
function computeMargin(g, basis) {
  if (!g) return null;
  const b = basis || {};
  // 매입가: lot 기준값 → top-level → 매입정보 배열 최신 항목 (growthdb.vue changeCost와 동일)
  // ⚠ '마지막 행' 이 아니라 '가격이 적힌 마지막 행' 이다 — 입고내역이 깔아주는 뼈대 행은
  //   가격이 비어 있어서 그대로 읽으면 원가가 0 이 되고 마진이 통째로 틀어진다.
  var cost = b.매입가 > 0 ? b.매입가 : g.매입가;
  if (!cost) {
    cost = lotCost.대표매입가(g.매입정보);
  }
  if (!cost) return null;
  const milkrun = b.밀크런 != null ? b.밀크런 : (g.밀크런비용 || 0);
  const fee = g.수수료 || null;
  let commission = g.commission || 0;
  if (fee && (b.배송비 != null || b.입출고비 != null)) {
    const 판매수수료 = Number(fee.판매수수료) || 0;
    const 배송비 = b.배송비 != null ? b.배송비 : (Number(fee.배송비) || 0);
    const 입출고비 = b.입출고비 != null ? b.입출고비 : (Number(fee.입출고비) || 0);
    commission = 판매수수료 + 배송비 + 입출고비;
  }
  const totalCost = cost + milkrun;
  return Math.round(((g.salePrice || 0) - (g.coupon || 0) - commission * 1.1 - totalCost) * 10 / 11);
}

/**
 * 옵션ID → 개별마진 맵 — 상품관리 화면(store applyGrowthMargin · growthdb.vue changeMargin)과 같은 규칙을 서버 광고 마진이 쓴다(#112).
 *   매입가 = 대표매입가(가격 적힌 마지막 행; 없으면 그 상품의 옵션 전부 마진 없음 → null), 반품 옵션 = 같은 값, 묶음 = × 묶음수.
 *   밀크런 = g.밀크런비용(묶음 × 묶음수). basis(옵션ID별, /api/growth/getUnitCostBasis 와 같은 값)가 있으면 computeMargin 이 lot·실제 배송비로 바꾼다.
 * @param {Array} growthDocs GrowthDB 문서(salePrice·coupon·commission·수수료·밀크런비용·returnProducts·upBundlingProducts)
 * @param {Object} purchaseInfoOf 본체 옵션ID → PurchaseDB 매입정보 배열
 * @param {Object} [basisMap] 옵션ID → { 매입가, 밀크런, 배송비, 입출고비 }
 * @returns {Map<string, number|null>}
 */
function growthMarginMap(growthDocs, purchaseInfoOf, basisMap) {
  const out = new Map();
  const basisOf = vid => (basisMap || {})[String(vid)];
  (growthDocs || []).forEach(d => {
    if (!d) return;
    const 대표 = lotCost.대표매입가((purchaseInfoOf || {})[String(d.vendorItemId)] || []);
    if (!(대표 > 0)) return;
    const milk = d.밀크런비용 || 0;
    const put = (a, cost, m) => {
      if (!a || a.vendorItemId == null || out.has(String(a.vendorItemId))) return;
      out.set(String(a.vendorItemId), computeMargin({ ...a, 매입가: cost, 밀크런비용: m }, basisOf(a.vendorItemId)));
    };
    put(d, 대표, milk);
    (d.returnProducts || []).forEach(r => put(r, 대표, milk));
    (d.upBundlingProducts || []).forEach(b => put(b, 대표 * (b.upBundlingSize || 1), milk * (b.upBundlingSize || 1)));
  });
  return out;
}

/**
 * 판매장부 개당이익(광고비 제외) — #112 사장님 결정: 재고손실보상은 개별마진에 넣지 않는다.
 *   재고손실보상은 쿠팡이 잃어버린 재고에 대한 일회성 보상이라 «한 개 팔 때 남는 돈»이 아니다.
 *   총이익(GP)에는 그대로 들어 있고(판매비용합계에서 빠짐 → 부가세 ×10/11 반영), 요약에 별도 줄로 보인다.
 *   개당이익 = (총이익 + 광고비 − 재고손실보상 × 10/11) ÷ 순판매   (growthdb 개별마진 computeMargin 과 같은 범위)
 * @returns {number|null} 총이익을 모르거나 순판매가 0 이하면 null
 */
function ledgerProfitPerUnit(grossProfit, adTotal, inventoryCompensation, qty) {
  if (grossProfit === null || grossProfit === undefined || !(qty > 0)) return null;
  return (grossProfit + (adTotal || 0) - (inventoryCompensation || 0) * 10 / 11) / qty;
}

/**
 * §0 단위 마진 (공급가 기반) — 상품관리(신규) changeMargin 공식과 동일(SoT).
 *   마진 = round((공급가 − 매입가) × 10/11)   (공급가 = 쿠팡 공급가/순매출, ×10/11 = VAT 제외)
 * GrowthDB에 마진이 없을 때 상품관리(신규) 매입가로 대체 산출하는 경로용.
 * 공급가/매입가가 없거나 공급가 ≤ 매입가면 산출 불가 → null.
 * @param {number} supplyPrice 공급가 (순매출)
 * @param {number} cost 매입가 (원가)
 * @returns {number|null}
 */
function computeMarginFromSupply(supplyPrice, cost) {
  const s = Number(supplyPrice) || 0;
  const c = Number(cost) || 0;
  if (s <= 0 || c <= 0 || s <= c) return null;
  return Math.round((s - c) * 10 / 11);
}

/** §2-3 CPC 한도(usl) = m × CVR (이 위로 올리면 적자) */
function cpcUsl(cvr, margin) {
  return cvr * margin;
}

/** §2-5 최적 CPC = usl/2, 10원 단위 내림, 최소 100 (순수익 최대 지점) */
function cpcOpt(usl) {
  const v = Math.floor(usl / 2 / 10) * 10;
  return v < CPC_MIN ? CPC_MIN : v;
}

/** §3 최소 CVR = 100 / margin (이 아래 CVR이면 광고 불가) */
function minCvr(margin) {
  return margin > 0 ? CPC_MIN / margin : 0;
}

/** §8 누적 광고ROI = (광고비 + 순수익) / 광고비 ( = 총수익/광고비 ) */
function adRoi(adCost, profit) {
  return adCost > 0 ? (adCost + profit) / adCost : 0;
}

// ───── ROAS 앵커 (§2-6, §15). 마진율 = margin / 판매가 ─────

/** §2-2 손익분기 ROAS = 1 / 마진율 */
function breakevenRoas(marginRate) {
  return marginRate > 0 ? 1 / marginRate : 0;
}

/** §2-6 최적 ROAS = 2 / 마진율 (= 손익분기 × 2) */
function optimalRoas(marginRate) {
  return marginRate > 0 ? 2 / marginRate : 0;
}

/** §2-6 최대 ROAS = S × CVR / 100 (CPC 최소) */
function maxRoas(salePrice, cvr) {
  return salePrice * cvr / CPC_MIN;
}

/** §15 ROAS → CPC 환산: CPC = S × CVR / ROAS */
function roasToCpc(salePrice, cvr, roas) {
  return roas > 0 ? salePrice * cvr / roas : 0;
}

/**
 * §10·§12 광고타입 분류 — 캠페인/광고그룹 구조 필드로 판정.
 * ⚠️ name·creationContext로 판정 금지 (§10). 반드시 구조 필드로.
 * @param {object} campaign tetris-api/campaigns 캠페인 객체 (groupList 포함)
 * @returns {'AI광고'|'매출최적화'|'매출스타트'|'수동키워드'|'수동스마트타겟팅'|'신규고객확보'|'기타'}
 */
function classifyCampaignType(campaign) {
  const group = campaign && campaign.groupList && campaign.groupList[0];

  // Step 0 — 광고 목표가 '매출 성장'이 아닌 캠페인 (§27.3): goalType 그대로 라벨 (광고센터 표기 따름)
  if (campaign.goalType && campaign.goalType !== 'SALES') {
    if (String(campaign.goalType).toUpperCase() === 'NCA') return '신규고객확보';
    return '기타'; // 인지도 등 — 성과형 분석 대상 아님
  }

  // Step 1 — 수동광고 (objective 우선, group bidType 보조)
  if (campaign.objective === 'PRODUCT_MANUAL' || (group && group.bidType === 'MANUAL_BID')) {
    // §12 수동 갈래: 스마트타겟팅형 vs 수동키워드형
    if (group && group.keywordTargeting === 'AUTOMATIC' && group.smartTargetingPricing != null) {
      return '수동스마트타겟팅';
    }
    return '수동키워드';
  }

  // Step 1.5 — 매출스타트 (§27.2): 명시 enum 판정 (2026-06-12 실데이터 확정 —
  // 이름에 '매출스타트'가 든 실캠페인들이 전부 objective=PRODUCT_TARGET_BUDGET / bidType=TARGET_SPEND).
  if (campaign.objective === 'PRODUCT_TARGET_BUDGET' || (group && group.bidType === 'TARGET_SPEND')) {
    return '매출스타트';
  }
  // 보조 시그니처: 자동입찰인데 목표 수익률이 캠페인·그룹 어디에도 없음 — 매출스타트는 목표
  // 광고수익률 입력칸 자체가 없는 타입이라 구조적으로 유일 (AI·매최는 100~10,000% 필수 입력).
  const noRoasTarget = (campaign.roasTarget == null || campaign.roasTarget === 0) &&
    (!group || group.roasTarget == null || group.roasTarget === 0);
  if (noRoasTarget) return '매출스타트';

  // Step 2 — 목표ROAS형 (AI vs 매출최적화)
  if (campaign.objective === 'PRODUCT_TARGET_ROAS') {
    if (group && group.adSelectionType === 'AUTO_SELECTION') return 'AI광고';
    return '매출최적화';
  }

  // §10 추가경고: 그 밖의 미지 objective — 매출최적화 등으로 잘못 버킷팅되면
  // "목표ROAS를 올려라" 같은 실행 불가능한 권장이 나가므로 분리한다.
  return '기타';
}

/**
 * §8 영역별 광고전략 판정 — adSourcing.vue getAdResult(줄 905~1005)와 **동일 로직**(추후 통일).
 *
 * 입력 단위 (adSourcing getAdResult와 일치):
 *   - cvr, minCvr : 0~1 비율 (예: 0.1 = 10%). minCvr = adDomain.minCvr(margin).
 *   - cpc, cpcUsl : 원(₩) 절대값. cpcUsl = adDomain.cpcUsl(cvr, margin).
 *   - clicks, orders : 정수.
 *   - validated : 검증완료(clicks≥11 && orders≥3, §4). false면 미검증 분기.
 *
 * 검증완료(validated) 분기 (getAdResult 905~1005, if 캐스케이드 — 구간 배타적):
 *   cvr < minCvr                          → 광고중지
 *   cpc ≥ usl                             → 광고중지
 *   usl*0.6 ≤ cpc < usl                   → 광고최소화
 *   usl*0.4 ≤ cpc < usl*0.6               → 광고유지
 *   100    ≤ cpc < usl*0.4                → 광고최적화
 *   cpc < 100                             → 광고중지
 * 미검증 분기:
 *   (orders+1)/clicks < minCvr            → 광고중지, 아니면 '' (판단보류)
 *
 * @param {{cvr:number, minCvr:number, cpc:number, cpcUsl:number, clicks:number, orders:number, validated:boolean}} p
 * @returns {'광고최적화'|'광고유지'|'광고최소화'|'광고중지'|''} 미검증·판단보류 시 빈 문자열
 */
function adStrategy(p) {
  const cvr = p.cvr, minCvr = p.minCvr, cpc = p.cpc, usl = p.cpcUsl;
  if (p.validated) {
    if (cvr < minCvr) return '광고중지';
    let result = '';
    if (usl <= cpc) result = '광고중지';
    if (usl * 0.6 <= cpc && usl > cpc) result = '광고최소화';
    if (usl * 0.4 <= cpc && usl * 0.6 > cpc) result = '광고유지';
    if (CPC_MIN <= cpc && usl * 0.4 > cpc) result = '광고최적화';
    if (CPC_MIN > cpc) result = '광고중지';
    return result;
  }
  // 미검증: 낙관적 CVR((orders+1)/clicks)도 최소CVR 미만이면 중지
  if (p.clicks > 0 && (p.orders + 1) / p.clicks < minCvr) return '광고중지';
  return '';
}

// ───── §16 재고커버리지 보간 (왼쪽: 효율/보존, 로켓그로스 품절임박용) ─────

/** §16 재고커버리지 R_cov = 재고기간 / 리드타임. 입고예정 없으면(L≤0) ∞(=충분). */
function stockCoverage(stockDays, leadTime) {
  if (!leadTime || leadTime <= 0) return Infinity;
  return stockDays / leadTime;
}

/** §16 긴급도 u = 1 − clamp(R_cov, 0, 1). (0=충분, 1=재고바닥) */
function coverageUrgency(rCov) {
  const c = rCov < 0 ? 0 : (rCov > 1 ? 1 : rCov);
  return 1 - c;
}

/** §16 목표CPC = 최적CPC·(1−u) + 100·u (최적CPC와 바닥 100 사이 보간) */
function coverageTargetCpc(optCpc, urgency) {
  return optCpc * (1 - urgency) + CPC_MIN * urgency;
}

/** §15 CPC → ROAS 환산: ROAS = S × CVR / CPC */
function cpcToRoas(salePrice, cvr, cpc) {
  return cpc > 0 ? salePrice * cvr / cpc : 0;
}

/**
 * §16 재고커버리지 보간 목표ROAS (로켓그로스 효율/보존).
 * 재고 충분(R_cov≥1) → 최적ROAS, 재고 바닥(R_cov≤0) → 최대ROAS.
 * @param {number} optCpc 최적CPC (m·CVR/2, cpcOpt 결과)
 * @param {number} stockDays 남은 재고기간(일)
 * @param {number} leadTime 입고 리드타임 L(일)
 * @returns {{rCov:number, urgency:number, targetCpc:number, targetRoas:number}}
 */
function coverageInterpolatedRoas(salePrice, cvr, optCpc, stockDays, leadTime) {
  const rCov = stockCoverage(stockDays, leadTime);
  const urgency = coverageUrgency(rCov);
  const targetCpc = coverageTargetCpc(optCpc, urgency);
  const targetRoas = cpcToRoas(salePrice, cvr, targetCpc);
  return { rCov, urgency, targetCpc, targetRoas };
}

// ───── §17 공격/투자 보간 (오른쪽). 로켓배송 품절임박 = 발주유도(속도↑) ─────

/** §17 공격 목표CPC = 최적CPC + (손익분기CPC − 최적CPC)·강도. 강도 0=중립(최적), 1=손익분기(본전). */
function attackTargetCpc(optCpc, breakevenCpc, intensity) {
  const k = intensity < 0 ? 0 : (intensity > 1 ? 1 : intensity);
  return optCpc + (breakevenCpc - optCpc) * k;
}

/**
 * §17 재고긴급도 기반 공격 목표ROAS (로켓배송: 품절임박 → 공격).
 * 왼쪽(§16)과 같은 긴급도 u를 쓰되 방향이 반대 — u↑일수록 손익분기 쪽으로(CPC↑/ROAS↓).
 * @param {number} optCpc 최적CPC (m·CVR/2)
 * @param {number} breakevenCpc 손익분기CPC (m·CVR = usl)
 * @returns {{rCov:number, intensity:number, targetCpc:number, targetRoas:number}}
 */
function attackInterpolatedRoas(salePrice, cvr, optCpc, breakevenCpc, stockDays, leadTime) {
  const rCov = stockCoverage(stockDays, leadTime);
  const intensity = coverageUrgency(rCov); // u: 재고바닥=1 → 최대 공격
  const targetCpc = attackTargetCpc(optCpc, breakevenCpc, intensity);
  const targetRoas = cpcToRoas(salePrice, cvr, targetCpc);
  return { rCov, intensity, targetCpc, targetRoas };
}

module.exports = {
  CPC_MIN,
  DEFAULT_MARGIN,
  integratedMarginSum,
  marginPerOrder,
  adProfit,
  computeMargin,
  growthMarginMap,
  ledgerProfitPerUnit,
  computeMarginFromSupply,
  cpcUsl,
  cpcOpt,
  minCvr,
  adRoi,
  breakevenRoas,
  optimalRoas,
  maxRoas,
  roasToCpc,
  cpcToRoas,
  adStrategy,
  classifyCampaignType,
  stockCoverage,
  coverageUrgency,
  coverageTargetCpc,
  coverageInterpolatedRoas,
  attackTargetCpc,
  attackInterpolatedRoas,
};
